import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { newsList } from "@/mocks/news";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((item) => item.id === id);
  const related = newsList.filter((item) => item.id !== id).slice(0, 3);
  const relatedMovies = article
    ? movies.filter((movie) => article.relatedMovies.includes(movie.id))
    : [];

  useSeo({
    title: article ? `${article.title} - 影视资讯 - 蜜桃成熟时电影` : "资讯未找到 - 蜜桃成熟时电影",
    description: article
      ? article.summary
      : "未找到对应的影视资讯内容，请返回资讯列表重新浏览。",
    keywords: article
      ? `${article.category},影视资讯,蜜桃成熟时电影,${article.title}`
      : "影视资讯,蜜桃成熟时电影",
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1240px] flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-article-line text-2xl" />
          </span>
          <h1 className="font-heading text-2xl font-semibold text-foreground-950">未找到这篇资讯</h1>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            内容可能已调整或链接有误，可以回到资讯列表查看最新更新。
          </p>
          <Link
            to="/news"
            className="mt-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影视资讯", to: "/news" },
              { label: article.category },
            ]}
          />
        </div>
      </div>

      <article className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-12">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-400">
              <Link
                to={`/news?category=${encodeURIComponent(article.category)}`}
                className="rounded-full bg-accent-100 px-2.5 py-1 text-accent-700 transition-colors hover:bg-accent-200"
              >
                {article.category}
              </Link>
              <span>{article.date}</span>
              <span className="h-3 w-px bg-background-300" />
              <span>撰文 / {article.author}</span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-3xl">
              {article.title}
            </h1>
            <p className="mt-3 border-l-2 border-primary-400 pl-3 text-sm leading-relaxed text-foreground-600">
              {article.summary}
            </p>

            <div className="mt-6 h-[220px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[360px]">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 影视资讯配图`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {article.content.map((block, index) => (
                <div key={index}>
                  {block.heading && (
                    <h2 className="mb-2 mt-6 flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                      <span className="h-3.5 w-1 rounded-full bg-accent-500" />
                      {block.heading}
                    </h2>
                  )}
                  <p className="text-sm leading-[2] text-foreground-700 md:text-[15px]">{block.text}</p>
                </div>
              ))}
            </div>

            {relatedMovies.length > 0 && (
              <div className="mt-10 rounded-lg bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                  <i className="ri-film-line text-accent-600" />
                  文中提及的影片
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {relatedMovies.map((movie) => (
                    <Link
                      key={movie.id}
                      to={`/detail/${movie.id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-accent-600"
                    >
                      <i className="ri-play-circle-line" />
                      《{movie.title}》
                      <span className="text-[10px] text-foreground-400">{movie.year}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-6">
              <Link
                to="/news"
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-foreground-600 transition-colors hover:text-accent-600"
              >
                <i className="ri-arrow-left-line" />
                返回资讯列表
              </Link>
              <div className="flex items-center gap-2 text-[11px] text-foreground-400">
                <span>内容分类：{article.category}</span>
                <span className="h-3 w-px bg-background-300" />
                <span>演示数据</span>
              </div>
            </div>
          </div>

          <aside className="min-w-0">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 lg:sticky lg:top-[100px]">
              <h2 className="font-heading text-sm font-semibold text-foreground-950">相关资讯</h2>
              <div className="mt-4 flex flex-col gap-4">
                {related.map((item) => (
                  <Link key={item.id} to={`/news/${item.id}`} className="group flex gap-3">
                    <span className="h-[58px] w-[78px] shrink-0 overflow-hidden rounded-sm bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="line-clamp-2 text-xs leading-relaxed text-foreground-800 transition-colors group-hover:text-accent-600">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[10px] text-foreground-400">{item.date}</span>
                    </span>
                  </Link>
                ))}
              </div>
              <Link
                to="/news"
                className="mt-5 flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-md bg-background-100 py-2.5 text-xs text-foreground-700 transition-colors hover:text-accent-600"
              >
                查看全部资讯
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </article>
    </SiteLayout>
  );
}